import type { KeyboardEvent } from "react";
import { ChangeEvent, FormEvent } from "react";
import { Button } from "components/atoms";
import styled, { keyframes } from "styled-components";

type Suggestion = {
  primaryLabel: string;
  secondaryLabel: string;
};

interface SearchFormProps {
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  inputValue: string;
  typedValue: string;
  suggestions: Suggestion[];
  isOpen: boolean;
  activeIndex: number;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
  onSuggestionHover: (index: number) => void;
  onSuggestionSelect: (index: number) => void;
  onSuggestionsMouseLeave: () => void;
}

const slideInFromBottom = keyframes`
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const SearchContainer = styled.div`
  animation: ${slideInFromBottom} 2s ease-out forwards;
  display: flex;
  justify-content: flex-end;
  position: relative;
  z-index: 2;
`;

const FormWrapper = styled.div`
  position: relative;
  width: min(520px, 80vw);
`;

const FinderForm = styled.form`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: relative;
`;

const ButtonLoupe = styled(Button)`
  cursor: pointer;
  position: absolute;
  right: 10px;
  top: calc(50% + 12px);
  transform: translateY(-50%);
  z-index: 2;
  height: 29px;
  width: 36px;
  background: none;
  border: none;
  border-radius: 45%;
  &:focus {
    outline: 2px solid #add8e6;
  }
`;

const TextInput = styled.input<{ $isPreviewing: boolean }>`
  margin-top: 3%;
  width: 100%;
  border-bottom: 2px solid lightblue;
  border-top: 1px solid lightblue;
  /* border-top: none; */
  border-left: none;
  border-right: none;
  /* border-left: 1px solid lightblue; */
  background: ${({ $isPreviewing }) =>
    $isPreviewing ? "rgba(24, 40, 72, 0.20)" : "transparent"};
  /* border-radius: 22% 28% 12% 61%; */
  border-radius: 9% 35% 5% 56%;
  padding-top: 14px;
  padding-bottom: 14px;
  padding-left: 19px;
  padding-right: 54px;
  box-shadow: ${({ $isPreviewing }) =>
    $isPreviewing ? "0 12px 30px rgba(0, 0, 0, 0.22)" : "none"};
  &:focus {
    outline: none;
  }
  &[type="search"] {
    font-size: 16px;
    color: #f5e2c8;
    font-family: "Finger Paint", cursive;
  }
  &::-webkit-search-cancel-button {
    display: none;
  }
`;

const SuggestionsPanel = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  width: 100%;
  border-radius: 16px;
  border: 1px solid rgba(245, 226, 200, 0.18);
  background: rgba(24, 40, 72, 0.72);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 18px 70px rgba(0, 0, 0, 0.35);
  overflow: hidden;
`;

const SuggestionsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 6px;
  max-height: 310px;
  overflow: auto;
  scrollbar-gutter: stable;
`;

const SuggestionItem = styled.li<{ $active: boolean }>`
  padding: 10px 12px;
  border-radius: 12px;
  cursor: pointer;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(173, 216, 230, 0.55)" : "rgba(245, 226, 200, 0.08)"};
  background: ${({ $active }) =>
    $active ? "rgba(173, 216, 230, 0.14)" : "rgba(24, 40, 72, 0.12)"};
  transition:
    background 120ms ease,
    border 120ms ease,
    transform 120ms ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(173, 216, 230, 0.55);
    background: rgba(173, 216, 230, 0.14);
  }
`;

const SuggestionPrimary = styled.div`
  font-family: "Finger Paint", cursive;
  color: rgba(245, 226, 200, 0.98);
  font-size: 0.98rem;
  letter-spacing: 0.2px;
  line-height: 1.15;
`;

const SuggestionSecondary = styled.div`
  margin-top: 3px;
  font-family: "Gaegu", cursive;
  color: rgba(245, 226, 200, 0.75);
  font-size: 1.05rem;
  line-height: 1.05;
`;

export const SearchForm = ({
  inputValue,
  typedValue,
  onSubmit,
  onChange,
  suggestions,
  isOpen,
  activeIndex,
  onKeyDown,
  onFocus,
  onBlur,
  onSuggestionHover,
  onSuggestionSelect,
  onSuggestionsMouseLeave,
}: SearchFormProps) => {
  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e);
  };

  const inputId = "country-finder";
  const listId = "country-finder-suggestions";
  const isPreviewing = isOpen && typedValue !== inputValue;

  return (
    <SearchContainer>
      <FormWrapper>
        <FinderForm onSubmit={onSubmit} id="finder" autoComplete="off">
          <label htmlFor={inputId} style={{ width: "100%" }}>
            <TextInput
              id={inputId}
              role="combobox"
              aria-label="Search countries"
              aria-autocomplete="list"
              aria-expanded={isOpen}
              aria-controls={listId}
              aria-haspopup="listbox"
              type="search"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              maxLength={80}
              value={inputValue}
              $isPreviewing={isPreviewing}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleOnChange(e)}
              onKeyDown={onKeyDown}
              onFocus={onFocus}
              onBlur={onBlur}
            />
          </label>

          <ButtonLoupe
            type="submit"
            hasImage
            image={require("assets/images/loupe.png")}
            onClick={() => {
              // Intentionally empty: form submission is handled by onSubmit.
            }}
            imgStyle={{ height: "100%" }}
            alt="loupe"
          />
        </FinderForm>

        {isOpen && suggestions.length > 0 && (
          <SuggestionsPanel
            role="listbox"
            id={listId}
            aria-label="Suggestions"
            onMouseLeave={onSuggestionsMouseLeave}
          >
            <SuggestionsList>
              {suggestions.map((s, idx) => (
                <SuggestionItem
                  key={`${s.primaryLabel}-${idx}`}
                  $active={idx === activeIndex}
                  onMouseEnter={() => onSuggestionHover(idx)}
                  onMouseDown={(e) => {
                    // Prevent input blur before selection happens.
                    e.preventDefault();
                    onSuggestionSelect(idx);
                  }}
                >
                  <SuggestionPrimary>{s.primaryLabel}</SuggestionPrimary>
                  <SuggestionSecondary>{s.secondaryLabel}</SuggestionSecondary>
                </SuggestionItem>
              ))}
            </SuggestionsList>
          </SuggestionsPanel>
        )}
      </FormWrapper>
    </SearchContainer>
  );
};
